import {
  AppBar,
  Toolbar,
  Typography,
  makeStyles,
  Button,
  IconButton,
  Drawer,
  Link,
  MenuItem,
  Box,
  Divider,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Logo from '../../assets/images/sportplatzgarage.svg'
import { SITE_PHONE } from '../../seo/siteSeo'

const PHONE_HREF = `tel:${SITE_PHONE.replace(/\s/g, '')}`;

const headersData = [
  {
    label: "Startseite",
    href: "/",
  },
  {
    label: "Nachrichten",
    href: "/nachrichten",
  },
  {
    label: "Dienstleistungen",
    href: "/dienstleistungen",
  },
  {
    label: "Marken",
    href: "/vag-garage-davos/",
  },
  {
    label: "Galerie",
    href: "/galerie",
  },
  {
    label: "Online Termin",
    href: "/online-termin",
  },
  {
    label: "Team",
    href: "/garage",
  },
];

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  header: {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    boxShadow: 'none',
    paddingRight: "20px",
    paddingLeft: "10px",
    paddingTop: 'env(safe-area-inset-top, 0px)',
    transition: 'background-color 0.35s ease, box-shadow 0.35s ease',
    "@media (max-width: 1300px)": {
      paddingLeft: '0px',
      paddingRight: '8px',
    },
  },
  logo: {
    "@media (max-width: 1300px)": {
      paddingLeft: '10px',
    },
  },
  menuButton: {
    fontFamily: '"Titillium Web", Titillium, sans-serif',
    fontWeight: 600,
    fontSize: "16px",
    marginLeft: "20px",
    marginRight: "4px",
    textTransform: 'none',
    color: 'inherit',
    '&.active': {
      borderBottom: '2px solid #f0bc17',
      borderRadius: 0,
    },
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      minHeight: 64,
      padding: theme.spacing(0.5, 1),
    },
  },
  drawerContainer: {
    padding: "20px 30px",
    paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
    width: 280,
    maxWidth: '86vw',
    backgroundColor: '#1b4995',
    height: '100%',
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  drawerItem: {
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(0.5, 0),
    borderRadius: '8px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
    },
  },
  logoImage: {
    height: '50px',
    maxWidth: 'min(220px, 58vw)',
    objectFit: 'contain',
    transition: 'transform 0.3s ease, filter 0.35s ease',
    [theme.breakpoints.down('sm')]: {
      height: '36px',
    },
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  mobileToolbar: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 64,
  },
  mobileLogo: {
    flex: 1,
    minWidth: 0,
  },
}));

export default function Header(props) {
  const classes = useStyles(props);

  const [state, setState] = useState({
    mobileView: false,
    drawerOpen: false,
    scrolled: props.hasHero === false,
  });

  const { mobileView, drawerOpen } = state;

  useEffect(() => {
    const setResponsiveness = () => {
      return window.innerWidth < 1300
        ? setState((prevState) => ({ ...prevState, mobileView: true }))
        : setState((prevState) => ({ ...prevState, mobileView: false }));
    };

    const readScrollY = () =>
      window.pageYOffset
      || document.documentElement.scrollTop
      || document.body.scrollTop
      || 0;

    let heroCoversHeader = Boolean(props.hasHero);

    const applyScrolled = () => {
      const isScrolled = readScrollY() > 40 || !heroCoversHeader;
      setState((prevState) => (
        prevState.scrolled === isScrolled
          ? prevState
          : { ...prevState, scrolled: isScrolled }
      ));
    };

    setResponsiveness();
    applyScrolled();
    window.addEventListener("resize", setResponsiveness);
    window.addEventListener("scroll", applyScrolled, { passive: true });
    document.documentElement.addEventListener("scroll", applyScrolled, { passive: true });
    document.body.addEventListener("scroll", applyScrolled, { passive: true });

    const hero = document.querySelector('.spg-hero');
    let observer;
    if (props.hasHero !== false && hero && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          heroCoversHeader = entry.isIntersecting && entry.intersectionRatio > 0;
          applyScrolled();
        },
        { threshold: [0, 0.01, 0.1], rootMargin: '-64px 0px 0px 0px' }
      );
      observer.observe(hero);
    } else {
      heroCoversHeader = false;
      applyScrolled();
    }

    return () => {
      window.removeEventListener("resize", setResponsiveness);
      window.removeEventListener("scroll", applyScrolled);
      document.documentElement.removeEventListener("scroll", applyScrolled);
      document.body.removeEventListener("scroll", applyScrolled);
      if (observer) observer.disconnect();
    };
  }, [props.hasHero]);

  const displayDesktop = () => {
    return (
      <Toolbar className={classes.toolbar}>
        {logoTitle}
        <Box display="flex" alignItems="center">
          {getMenuButtons()}
          {panneButton('desktop')}
        </Box>
      </Toolbar>
    );
  };

  const displayMobile = () => {
    const handleDrawerOpen = () =>
      setState((prevState) => ({ ...prevState, drawerOpen: true }));
    const handleDrawerClose = () =>
      setState((prevState) => ({ ...prevState, drawerOpen: false }));

    return (
      <Toolbar className={classes.mobileToolbar}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          aria-haspopup="true"
          onClick={handleDrawerOpen}
          style={{ marginRight: '8px' }}
        >
          <MenuIcon />
        </IconButton>
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerClose}
          PaperProps={{
            style: {
              backgroundColor: '#1b4995',
            }
          }}
        >
          <div className={classes.drawerContainer}>
            <div className={classes.drawerHeader}>
              <RouterLink to='/' onClick={handleDrawerClose}>
        <img className={classes.logoImage} src={Logo} alt="Sportplatzgarage Gutknecht – stop+go Davos" style={{ height: '40px', filter: 'brightness(0) invert(1)' }} />
              </RouterLink>
              <IconButton onClick={handleDrawerClose} style={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </div>
            <Divider style={{ backgroundColor: 'rgba(255,255,255,0.1)', margin: '16px 0' }} />
            {getDrawerChoices(handleDrawerClose)}
            <Box mt={2}>
              {panneButton('drawer', handleDrawerClose)}
            </Box>
          </div>
        </Drawer>
        <div className={classes.mobileLogo}>{logoTitle}</div>
        {panneButton('mobile')}
      </Toolbar>
    );
  };

  const getDrawerChoices = (onClose) => {
    return headersData.map(({ label, href }) => {
      const isActive =
        props.currentLocation === href
        || props.currentLocation === href.replace(/\/$/, '')
        || (href.includes('vag-garage') && String(props.currentLocation || '').includes('garage-davos'));
      return (
        <Link
          component={RouterLink}
          to={href}
          onClick={onClose}
          key={label}
          style={{ textDecoration: "none" }}
        >
          <MenuItem 
            className={classes.drawerItem}
            style={{ 
              color: isActive ? '#f0bc17' : 'white',
              fontFamily: '"Titillium Web", Titillium, sans-serif',
              backgroundColor: isActive ? 'rgba(20, 160, 219, 0.1)' : 'transparent',
            }}
          >
            {label}
          </MenuItem>
        </Link>
      );
    });
  };

  const logoTitle = (
    <Typography variant="h6" component="h1" className={classes.logo}>
      <RouterLink to='/' style={{ textDecoration: 'none' }}>
        <img
          className={classes.logoImage}
          src={Logo}
          alt="Sportplatzgarage Gutknecht – stop+go Davos"
          style={{
            filter: state.scrolled
              ? 'none'
              : 'drop-shadow(0 2px 10px rgba(0,0,0,0.45))',
          }}
        />
      </RouterLink>
    </Typography>
  );

  const panneButton = (variant, onClick) => {
    const isMobile = variant === 'mobile';
    const isDrawer = variant === 'drawer';
    return (
      <a
        href={PHONE_HREF}
        className={`spg-panne-btn${isMobile ? ' is-compact' : ''}${isDrawer ? ' is-drawer' : ''}`}
        aria-label={`Pannendienst anrufen: ${SITE_PHONE}`}
        onClick={onClick}
      >
        <span className="spg-panne-btn-label">Panne?</span>
        {!isMobile ? <span className="spg-panne-btn-phone">{SITE_PHONE}</span> : null}
      </a>
    );
  };

  const getMenuButtons = () => {
    return headersData.map(({ label, href }) => {
      const isActive =
        props.currentLocation === href
        || props.currentLocation === href.replace(/\/$/, '')
        || (href.includes('vag-garage') && String(props.currentLocation || '').match(/(audi|vw|skoda|seat|cupra|vag|nutzfahrzeuge).*davos/));
      return (
        <Button
          key={label}
          color="inherit"
          to={href}
          size="large"
          component={RouterLink}
          className={`${classes.menuButton} ${isActive ? 'active' : ''}`}
        >
          {label}
        </Button>
      );
    });
  };

  return (
    <header>
      <AppBar
        color="inherit"
        className={`${classes.header} spg-header${state.scrolled ? ' is-solid' : ''}`}
        position={props.fixed ? "fixed" : "static"}
      >
        {mobileView ? displayMobile() : displayDesktop()}
      </AppBar>
    </header>
  );
}
